import React, { useCallback, useEffect } from 'react';
import { css, Theme, useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";

import { useAppState } from "core/contexts/AppContext";
import useChevron from "core/hooks/useChevron";
import useSetState from "core/hooks/useSetState";
import RoundChevronButton from "core/Components/Buttons/RoundChevronButton";
import Gallery from "core/Components/Item/Gallery";
import ItemChevron from "core/Components/Item/ItemChevron";
import ItemSection from "core/Components/Item/ItemSection";
import ItemWrapper from "core/Components/Item/ItemWrapper";
import LeftContainer from "core/Components/Item/LeftContainer";
import MainHeading from "core/Components/Item/MainHeading";
import Quote from "core/Components/Item/Quote";
import RightContainer from "core/Components/Item/RightContainer";
import SectionContainer from "core/Components/SectionContainer";
import VideoPlayer from "core/Components/Video/VideoPlayer";
import VideoViewContainer from "core/Components/Video/VideoViewContainer";
import VideoViewLayer from "core/Components/Video/VideoViewLayer";
import Writer from "core/Components/Item/Writer";
import { getEffectiveCountry } from "core/includes/countries";
import { hasWindow } from "core/includes/window";
import { analytics } from "core/includes/ga4EcommerceTracker";
import { useCountryAndCurrency } from "core/hooks/useCountryAndCurrency";

import RedirectPage from 'pages/RedirectPage';
import ItemDescription from "pages/Components/ProductPage/ItemDescription";
import ItemOutOfStock from "pages/Components/ProductPage/ItemOutOfStock";
import ItemPriceBox from "pages/Components/ProductPage/ItemPriceBox";
import Description from "pages/Components/CartAndCheckout/components/Description";
import ConfirmationModal from "pages/Components/CartAndCheckout/components/ConfirmationModal";
import ProductItemCartButton from "pages/Components/ProductListPage/components/ProductItemCartButton";

import { getDeliveryCountries } from '../ProductListPage/includes/includes';
import useCart from '../CartPage/hooks/useCart';
import usePrice from "core/hooks/usePrice";

type DefaultState = {
    product?: ProductPage | null,
    quantity: number,
    videoPath: string,
    showVideo: boolean,
    isProcessing: boolean,
    showConfirmationCartModal: boolean,
};

const INITIAL_STATE = {
    quantity: 1,
    videoPath: '',
    isProcessing: false,
    showConfirmationCartModal: false,
};

type Props = {
    data: ProductItem,
};

const DEFAULT_BACKGROUND_COLOUR = '#1D88DB';

const ProductPage: React.FC<Props> = ({ data: product }) => {
    const theme: Theme = useTheme();
    const navigate = useNavigate();

    const [appState] = useAppState();

    const firstVideoPath = product.imagesAndVideos?.find(
        (item: ImageAndVideo) => !!item.videoPath
    )?.videoPath ?? '';

    const [state, setState] = useSetState<DefaultState>({
        ...INITIAL_STATE,
        videoPath: firstVideoPath,
        showVideo: false,
    });

    const { updateCart } = useCart();
    const { currency } = useCountryAndCurrency(appState.countryCode.code);
    const { chevronSize, chevronColour, chevronBackground, chevronBackgroundHover } = useChevron();
    const rightSectionBackgroundColour = product.themeColour || DEFAULT_BACKGROUND_COLOUR;

    const { amount, discountedPrice } = usePrice(product.price, appState.globalCurrencyOptions.isGlobal);

    const productDeliveryCountries = getDeliveryCountries(product);

    const isProductOutOfStock =
        !product ||
        product.outOfStock ||
        !product.availableForSale ||
        (product.inventoryQuantity !== undefined && product.inventoryQuantity <= 0)
    ;

    const onShowVideoClick = (videoPath: string) => setState({ videoPath, showVideo: true });
    const onCloseVideoClick = () => setState({ showVideo: false });

    const updateItemQuantity = (productId: string, productQuantity: number) => {
        if (appState.cart && appState.cart.countryCode !== appState.countryCode.code) {
            return new Promise((resolve) => resolve(setState({ showConfirmationCartModal: true })));
        } else {
            return updateCart({ productId: parseInt(productId), productQuantity: productQuantity });
        }
    }

    const closeCountryCheckoutPopup = useCallback(() => {
        setState({ showConfirmationCartModal: false });
    }, [setState]);

    const onCart = useCallback(() => {
        closeCountryCheckoutPopup();
        navigate('/cart-summary')
    }, [closeCountryCheckoutPopup, navigate]);

    // GA4 eCommerce
    useEffect(() => {
        if (product && currency) {
            analytics.viewItem(product, currency);
        }
    }, [product, currency]);

    // Redirect user if the product is not available in their country
    if (hasWindow && !productDeliveryCountries.includes(getEffectiveCountry(appState.countryCode.code.toLowerCase()))) {
        return <RedirectPage url={'/classroom-resources'} />
    }

    const countryConfirmationModalStyle = css`
        .confirmation-modal-content {
            margin: auto 0;
        }
    `;

    const cartButtonContainer = css`
        display: flex;
        justify-content: center;
        margin-top: 28px;
    `;

    return (
        <>
            {isProductOutOfStock && <ItemOutOfStock />}
            <SectionContainer>
                {product && (
                    <>
                        <ItemChevron chevronSize={chevronSize}>
                            <RoundChevronButton
                                type={"left"}
                                onClick={() => navigate(-1)}
                                roundColour={chevronBackground}
                                roundHoverColour={chevronBackgroundHover}
                                chevronColour={chevronColour}
                                size={chevronSize}
                            />
                        </ItemChevron>
                        <ItemWrapper>
                            <ItemSection fontColour={"black"}>
                                <LeftContainer>
                                    {product.imagesAndVideos && (
                                        <Gallery
                                            imagesAndVideos={product.imagesAndVideos}
                                            onVideoClick={onShowVideoClick}
                                        />
                                    )}
                                </LeftContainer>
                            </ItemSection>
                            <ItemSection
                                fontColour={theme.colours.white}
                                backgroundColour={rightSectionBackgroundColour}
                            >
                                <RightContainer>
                                    <MainHeading text={product.title} />
                                    <ItemPriceBox
                                        price={amount}
                                        discountPrice={discountedPrice}
                                        shouldDiscount={product.hasOnGoingDiscount && !!discountedPrice}
                                        isTaxable
                                        isTaxUnderPrice
                                    />
                                    <ProductItemCartButton
                                        css={cartButtonContainer}
                                        product={product}
                                        updateItemQuantity={updateItemQuantity}
                                    />
                                    <ItemDescription>
                                        {product.productDescription || product.description}
                                    </ItemDescription>
                                    <Quote text={product.quote} useDoubleQuotes />
                                    <Writer>{product.writer}</Writer>
                                    <Description marginTop={30}>{product.productDisclaimer?.value ?? ''}</Description>
                                </RightContainer>
                            </ItemSection>
                        </ItemWrapper>
                    </>
                )}

                {firstVideoPath && (
                    <VideoViewContainer
                        clearPlayVideo={onCloseVideoClick}
                        isVideoShowing={state.showVideo}
                    >
                        <VideoViewLayer>
                            <VideoPlayer videoPath={state.videoPath} autoPlay={state.showVideo} />
                        </VideoViewLayer>
                    </VideoViewContainer>
                )}
                {state.showConfirmationCartModal && (
                    <ConfirmationModal
                        message={
                            <>
                                <div>
                                    Oops! &nbsp;This product doesn’t match your region.
                                </div>
                                <br />
                                <div>
                                    Please check out your cart first, then change country to browse products in your
                                    region.
                                </div>
                            </>
                        }
                        confirmLabel={"Go to cart"}
                        onCancel={closeCountryCheckoutPopup}
                        onConfirm={onCart}
                        css={countryConfirmationModalStyle}
                    />
                )}
            </SectionContainer>
        </>
    );
};

export default ProductPage;
